<script setup lang="ts">
import { ref, computed } from 'vue';

import GreenMarkIcon from '../../assets/icons/green-mark.svg?component';
import WhiteCopyIcon from '../../assets/icons/white-copy.svg?component';
import BlackCopyIcon from '../../assets/icons/copy.svg?component';

type Props = {
  copyText?: string | undefined;
  tooltipText?: string;
  tooltipSuccessText?: string;
  iconHoverBackground?: string;
  iconDark?: boolean;
  resolveCopyText?: () => Promise<string>;
  iconSize?: string | number;
  buttonSize?: string | number;
};

const props = withDefaults(defineProps<Props>(), {
  copyText: '',
  tooltipText: '',
  tooltipSuccessText: 'Copied',
  iconHoverBackground: '#3f3f46',
  iconDark: false,
  resolveCopyText: undefined,
  iconSize: '1rem',
  buttonSize: '2.5rem',
});

type CopyState = 'idle' | 'loading' | 'success';
const copyState = ref<CopyState>('idle');

async function handleClick() {
  if (copyState.value === 'loading') return;
  copyState.value = 'loading';

  try {
    const textToCopy = props.resolveCopyText
      ? await props.resolveCopyText()
      : (props.copyText ?? '');

    if (!textToCopy) throw new Error('No text to copy');
    await navigator.clipboard.writeText(textToCopy);

    copyState.value = 'success';
    setTimeout(() => {
      if (copyState.value === 'success') copyState.value = 'idle';
    }, 1000);
  } catch (err) {
    console.warn('Failed to copy text:', err);
    copyState.value = 'idle';
  }
}

const isLoading = computed(() => copyState.value === 'loading');
const showCopied = computed(() => copyState.value === 'success');

const tooltipConfig = computed(() => ({
  value: isLoading.value
    ? 'Loading…'
    : showCopied.value
      ? props.tooltipSuccessText
      : `Copy ${props.tooltipText}`,
  showDelay: showCopied.value ? 0 : 2000,
}));
</script>

<template>
  <div :key="tooltipConfig.value" v-tooltip.top="tooltipConfig" class="copy-btn">
    <Button
      class="copy-btn__button"
      rounded
      variant="text"
      :icon-class="['copy-btn__icon']"
      aria-label="Copy"
      :disabled="isLoading"
      v-bind="$attrs"
      @click.stop="handleClick"
    >
      <template #icon>
        <template v-if="isLoading">
          <i class="pi pi-spin pi-spinner" style="font-size: 1rem; color: #22c55e"></i>
        </template>
        <template v-else-if="showCopied">
          <GreenMarkIcon class="copy-btn__icon copy-btn__icon--success" :width="iconSize" />
        </template>
        <template v-else>
          <BlackCopyIcon v-if="iconDark" class="copy-btn__icon" :width="iconSize" />
          <WhiteCopyIcon v-else class="copy-btn__icon" :width="iconSize" />
        </template>
      </template>
    </Button>
  </div>
</template>

<style lang="scss">
.copy-btn {
  --button-size: v-bind(props.buttonSize);

  position: relative;
  display: inline-block;

  &__button {
    --p-button-text-primary-hover-background: v-bind(props.iconHoverBackground);
    width: var(--button-size);
    height: var(--button-size);
  }

  &__icon {
    color: white;
    display: inline-block;
    vertical-align: middle;

    &--success {
      animation: flash 1s ease-out;
    }
  }
}

@keyframes flash {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 0;
    transform: scale(1);
  }
}
</style>
